/**
 * Tutor registration detail tab.
 * Completion bar, onboarding checklist, and identity / contact / qualification blocks.
 */
import * as React from 'react';
import {
  CheckCircle2,
  User,
  UserCheck,
  BookOpen,
  FileCheck,
  FileText,
  XCircle,
  Globe,
  MapPin,
  Briefcase,
} from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { DetailAvailabilityGrid } from '@/components/common/details/DetailAvailabilityGrid';
import type { WeeklyAvailabilitySlot } from '@/types/user.types';
import { Collapsible } from '@/components/ui/collapsible';
import { flattenProfessionalSituation } from '@/utils/professional-situation-utils';
import {
  ENGAGEMENT_VALORISATION_LABEL,
  PRO_SITUATION_COLOR_MAP,
  PRO_SITUATION_STATUS_LABEL,
  REMOTE_SUBJECT_CONFIG,
  STEP_COMPLETION_CONFIG,
  TUTOR_REMOTE_STEP_LABELS,
} from '@/components/tutors/tutor-configs';
import type { TutorRecord } from '@/types/remote/tutor.types';
import {
  capitalizeFirst,
  completionBarColor,
  daysSince,
  formatCityInfo,
  formatDate,
  formatLevelName,
  formatPartnerName,
  getCivilTitleLabel,
  getCompletionStatus,
  getCoursePathLabel,
  getProgramLabel,
  getSupportTypeLabel,
  normalizeDetailWeeklyAvailabilitySlots,
} from '@/components/tutors/remote/tutor-table-utils';
import {
  formatRemoteEngagementDuration,
  formatYearsOfEngagement,
} from '@/components/tutors/remote/tutor-form-utils';
import { resolveCriminalRecordDisplay } from '@/utils/criminal-record-utils';
import { renderProfessionalSituationDetails } from './TutorDetailsTab';
import { useTheme } from '@/hooks/useTheme';

/** Percent badge colored by onboarding-step completion. */
function CompletionBadge({ pct }: Readonly<{ pct: number }>) {
  const completionStatus = getCompletionStatus(pct);
  const completionConfig = STEP_COMPLETION_CONFIG[completionStatus];
  return (
    <LabelBadge
      label={`${pct}%`}
      icon={completionConfig.icon}
      variant={completionConfig.badgeVariant}
      color={completionConfig.badgeColor}
      size="sm"
    />
  );
}

/** Props for {@link TutorInfoSectionContent}. */
interface InfoSectionProps {
  readonly sections: Record<string, unknown>;
  readonly departments: { code: string; name: string }[];
}

/** Identity, contact, and qualification blocks. */
function TutorInfoSectionContent({ sections, departments }: InfoSectionProps) {
  const myInfo = sections['my_information'] as any;
  const pct = myInfo?.sectionCompletion?.completionPercentage ?? 0;
  const basicInfo = sections['basic_information'] as any;
  const personalInfo = sections['personal_information'] as any;
  const qualifInfo = sections['qualification_information'] as any;

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <CompletionBadge pct={pct} />
      </div>
      <TutorIdentityBlock basicInfo={basicInfo} />
      <TutorContactBlock personalInfo={personalInfo} departments={departments} />
      <div className="pt-4 border-t border-border">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
          Qualification
        </h4>
        {qualifInfo ? (
          <TutorQualificationContent qualifInfo={qualifInfo} />
        ) : (
          <p className="text-sm text-muted-foreground">Aucune donnée</p>
        )}
      </div>
    </>
  );
}

/** Outlined badge for a yes/no boolean. */
function BoolBadge({
  value,
  trueLabel,
  falseLabel,
}: Readonly<{ value: boolean; trueLabel: string; falseLabel: string }>) {
  return (
    <LabelBadge
      label={value ? trueLabel : falseLabel}
      icon={value ? CheckCircle2 : XCircle}
      variant="outlined"
      color={value ? 'rgb(var(--color-success))' : 'rgb(var(--color-error))'}
      size="sm"
    />
  );
}

/** Props for {@link TutorIdentityBlock}. */
interface IdentityBlockProps {
  readonly basicInfo: any;
}
/** Basic identity fields on the registration tab. */
function TutorIdentityBlock({ basicInfo }: IdentityBlockProps) {
  return (
    <div className="mb-4">
      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">Identité</h4>
      {basicInfo ? (
        <div className="space-y-3">
          <DetailField
            label="Nom / Prénom"
            value={`${basicInfo.firstName ?? '—'} ${basicInfo.lastName ?? '—'}`}
          />
          <DetailField label="Email" value={basicInfo.email} />
          <DetailField label="Téléphone" value={basicInfo.phoneNumber} />
          <DetailField label="Civilité" value={getCivilTitleLabel(basicInfo.gender)} />
          <DetailField
            label="Partage téléphone"
            value={
              <BoolBadge
                value={basicInfo.acceptSharePhone}
                trueLabel="Autorisé"
                falseLabel="Non autorisé"
              />
            }
          />
          <DetailField
            label="Email vérifié"
            value={
              <BoolBadge
                value={basicInfo.emailVerified}
                trueLabel="Vérifié"
                falseLabel="Non vérifié"
              />
            }
          />
          <DetailField
            label="Téléphone vérifié"
            value={
              <BoolBadge
                value={basicInfo.phoneVerified}
                trueLabel="Vérifié"
                falseLabel="Non vérifié"
              />
            }
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Aucune donnée</p>
      )}
    </div>
  );
}

/** Props for {@link TutorContactBlock}. */
interface ContactBlockProps {
  readonly personalInfo: any;
  readonly departments: { code: string; name: string }[];
}
/** Address, partner, and professional-situation fields. */
function TutorContactBlock({ personalInfo, departments }: ContactBlockProps) {
  const { companyName } = useTheme();

  const proSit = flattenProfessionalSituation(personalInfo?.professionalSituation);
  return (
    <div className="mb-4 pt-4 border-t border-border">
      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
        Coordonnées & profil
      </h4>
      {personalInfo ? (
        <div className="space-y-3">
          <DetailField label="Adresse" value={personalInfo.address} />
          <DetailField
            label="Ville"
            value={formatCityInfo(personalInfo.zipCode, personalInfo.city)}
          />
          <DetailField label="Pays" value={capitalizeFirst(personalInfo.country)} />
          <DetailField label="Département" value={personalInfo.department?.departmentName} />
          <DetailField
            label="Date de naissance"
            value={
              personalInfo.dateOfBirth
                ? `${formatDate(personalInfo.dateOfBirth)} (${Math.floor((Date.now() - new Date(personalInfo.dateOfBirth).getTime()) / (1000 * 60 * 60 * 24 * 365))} ans)`
                : '—'
            }
          />
          <DetailField
            label={`Partenaire ${companyName}`}
            value={
              <LabelBadge
                label={personalInfo.isPartner ? 'Partenaire' : 'Non partenaire'}
                icon={personalInfo.isPartner ? CheckCircle2 : XCircle}
                variant="outlined"
                color={
                  personalInfo.isPartner ? 'rgb(var(--color-success))' : 'rgb(var(--color-error))'
                }
                size="sm"
              />
            }
          />
          {personalInfo.isPartner && (
            <DetailField label="Nom partenaire" value={formatPartnerName(personalInfo.partner)} />
          )}
          {proSit?.status && PRO_SITUATION_COLOR_MAP[proSit.status] && (
            <>
              <div className="pt-2 border-t border-border">
                <DetailField
                  label="Situation professionnelle"
                  value={
                    <LabelBadge
                      label={PRO_SITUATION_STATUS_LABEL[proSit.status] ?? proSit.status}
                      icon={Briefcase}
                      variant="outlined"
                      color={
                        PRO_SITUATION_COLOR_MAP[proSit.status] ?? 'rgb(var(--color-1-nuance-1))'
                      }
                      size="sm"
                    />
                  }
                />
              </div>
              {renderProfessionalSituationDetails(proSit, departments)}
            </>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Aucune donnée</p>
      )}
    </div>
  );
}

/** Props for {@link TutorQualificationContent}. */
interface QualifProps {
  readonly qualifInfo: any;
}

/** Support type, levels, and engagement duration. */
function TutorQualificationContent({ qualifInfo }: QualifProps) {
  const st = qualifInfo.supportType as string | undefined;
  const isRemote = st === 'remote';
  const isOnsite = st === 'in_person';

  let supportIcon = BookOpen;
  if (isRemote) supportIcon = Globe;
  else if (isOnsite) supportIcon = MapPin;

  let supportColor = 'rgb(var(--color-1-nuance-1))';
  if (isRemote) supportColor = 'rgb(var(--color-info))';
  else if (isOnsite) supportColor = 'rgb(var(--color-3-nuance-1))';

  return (
    <div className="space-y-3">
      <DetailField
        label="Statut"
        value={
          <LabelBadge
            label={
              PRO_SITUATION_STATUS_LABEL[qualifInfo.userStatus] ?? qualifInfo.userStatus ?? '—'
            }
            icon={Briefcase}
            variant="outlined"
            color={PRO_SITUATION_COLOR_MAP[qualifInfo.userStatus] ?? 'rgb(var(--color-1-nuance-1))'}
            size="sm"
          />
        }
      />
      <DetailField
        label="Type d'accompagnement"
        value={
          <LabelBadge
            label={getSupportTypeLabel(st)}
            icon={supportIcon}
            variant="outlined"
            color={supportColor}
            size="sm"
          />
        }
      />
      <DetailField
        label="Niveaux accompagnés souhaités"
        value={
          Array.isArray(qualifInfo.levelsToSupport)
            ? qualifInfo.levelsToSupport.join(', ')
            : (qualifInfo.levelsToSupport ?? '—')
        }
      />
      <DetailField
        label="Engagement (durée)"
        value={formatYearsOfEngagement(qualifInfo.yearsOfEngagement)}
      />
    </div>
  );
}

/** Props for {@link TutorAccompagnementContent}. */
interface AccompSectionProps {
  readonly sections: Record<string, unknown>;
  readonly programs: any[];
}

/** Program, availability, and engagement blocks. */
function TutorAccompagnementContent({ sections, programs }: AccompSectionProps) {
  const mySupport = sections['my_support'] as any;
  const pct = mySupport?.sectionCompletion?.completionPercentage ?? 0;
  const programData = sections['program_data'] as any;
  const matchingData = sections['matching_data'] as any;
  const engagementData = sections['engagement_data'] as any;

  const validSlots: WeeklyAvailabilitySlot[] = normalizeDetailWeeklyAvailabilitySlots(
    matchingData?.availability ?? mySupport?.availability,
  );
  const weeklyHours = engagementData?.targetHours ?? mySupport?.targetHours;
  const engagementStartDate =
    engagementData?.engagementStartDate ??
    engagementData?.desiredStartDate ??
    mySupport?.engagementStartDate ??
    mySupport?.desiredStartDate;
  const engagementEndDate = engagementData?.engagementEndDate ?? mySupport?.engagementEndDate;
  const hasEngagement = Boolean(engagementData || mySupport);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <CompletionBadge pct={pct} />
      </div>

      <div className="mb-4">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">Programme</h4>
        {programData ? (
          <div className="space-y-3">
            <DetailField
              label="Type d'accompagnement"
              value={getSupportTypeLabel(programData.tutoringType)}
            />
            <DetailField label="Parcours" value={getCoursePathLabel(programData.coursePath)} />
            {Array.isArray(programData.programs) && programData.programs.length > 0 ? (
              <div>
                <span className="detail-field-label text-xs text-muted-foreground">
                  Programme(s)
                </span>
                <DetailChipList
                  items={programData.programs.map((p: any, i: number) => {
                    if (typeof p === 'string') {
                      return { id: p, label: getProgramLabel(p, programs) };
                    }
                    return {
                      id: String(p?.programId ?? p?.id ?? i),
                      label:
                        p?.programName ??
                        p?.name ??
                        p?.abbreviation ??
                        p?.programId ??
                        `Programme ${i + 1}`,
                    };
                  })}
                  colorScheme="color-3"
                  variant="outlined"
                />
              </div>
            ) : (
              <DetailField label="Programme(s)" value="—" />
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Aucune donnée</p>
        )}
      </div>

      <div className="mb-4 pt-4 border-t border-border">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
          Disponibilités
        </h4>
        {matchingData ? (
          <TutorAvailabilityContent matchingData={matchingData} validSlots={validSlots} />
        ) : (
          <p className="text-sm text-muted-foreground">Aucune donnée</p>
        )}
      </div>

      <div className="pt-4 border-t border-border">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">Engagement</h4>
        {hasEngagement ? (
          <div className="space-y-3">
            <DetailField
              label="Nombre d'élèves"
              value={
                engagementData?.numberOfStudentsToSupport ?? mySupport?.numberOfStudentsToSupport
              }
            />
            {weeklyHours != null && weeklyHours !== '' ? (
              <DetailField label="Heures par semaine" value={weeklyHours} />
            ) : null}
            <DetailField
              label="Engagement associatif valorisé"
              value={
                ENGAGEMENT_VALORISATION_LABEL[
                  engagementData?.valorisation ?? mySupport?.valorisation ?? ''
                ] ?? '—'
              }
            />
            <DetailField
              label="Durée"
              value={formatRemoteEngagementDuration({
                startDate: engagementStartDate,
                endDate: engagementEndDate,
                durationYears:
                  engagementData?.engagementDurationYears ?? mySupport?.engagementDurationYears,
                durationMonths:
                  engagementData?.engagementDurationMonths ?? mySupport?.engagementDurationMonths,
              })}
            />
            <DetailField label="Début souhaité" value={formatDate(engagementStartDate)} />
            <DetailField label="Fin prévue" value={formatDate(engagementEndDate)} />
            <DetailField
              label="Informations équipe"
              value={
                engagementData?.engagementNotes ??
                engagementData?.teamInformation ??
                mySupport?.engagementNotes ??
                mySupport?.teamInformation
              }
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Aucune donnée</p>
        )}
      </div>
    </>
  );
}

/** Props for {@link TutorAvailabilityContent}. */
interface AvailabilityContentProps {
  readonly matchingData: any;
  readonly validSlots: WeeklyAvailabilitySlot[];
}

/** Subject and weekly availability slots. */
function TutorAvailabilityContent({ matchingData, validSlots }: AvailabilityContentProps) {
  return (
    <div className="space-y-3">
      <DetailField
        label="Matière"
        value={
          matchingData.subjectToTeach ? (
            <LabelBadge
              label={
                REMOTE_SUBJECT_CONFIG[matchingData.subjectToTeach]?.label ??
                matchingData.subjectToTeach
              }
              icon={REMOTE_SUBJECT_CONFIG[matchingData.subjectToTeach]?.icon}
              variant={
                REMOTE_SUBJECT_CONFIG[matchingData.subjectToTeach]?.badgeVariant ?? 'outlined'
              }
              color={REMOTE_SUBJECT_CONFIG[matchingData.subjectToTeach]?.badgeColor}
            />
          ) : (
            '—'
          )
        }
      />
      {Array.isArray(matchingData.level) && matchingData.level.length > 0 ? (
        <div>
          <span className="text-xs text-muted-foreground detail-field-label">Niveaux</span>
          <DetailChipList
            items={matchingData.level.map((l: any, i: number) => ({
              id: String(l?.levelId ?? l?.id ?? i),
              label:
                typeof l === 'object'
                  ? (l?.levelName ?? l?.name ?? l?.label ?? l?.levelId ?? String(i))
                  : String(l),
            }))}
            colorScheme="color-2"
            variant="outlined"
          />
        </div>
      ) : (
        <DetailField label="Niveaux" value={formatLevelName(matchingData.level)} />
      )}
      <DetailField
        label="Créneaux hebdomadaires"
        value={
          validSlots.length > 0 ? (
            <DetailAvailabilityGrid slots={validSlots} variant="compact" />
          ) : (
            '—'
          )
        }
      />
      <DetailField
        label="Dispos spéciales"
        value={
          [
            matchingData.availableOnWeekends && 'Week-ends',
            matchingData.availableOnHolidays && 'Jours fériés',
            matchingData.availableOnVacations && 'Vacances',
          ]
            .filter(Boolean)
            .join(', ') || '—'
        }
      />
    </div>
  );
}

/** Props for {@link TutorRegistrationTab}. */
interface TutorRegistrationTabProps {
  record: TutorRecord;
  sections: Record<string, unknown>;
  completion: TutorRecord['completion'];
  fullState:
    | {
        templates?: Array<{ key: string; sortOrder: number; label?: string; displayName?: string }>;
      }
    | undefined;
  departments: { code: string; name: string }[];
  programs: any[];
}

/** Tutor registration detail tab. */
export const TutorRegistrationTab: React.FC<TutorRegistrationTabProps> = ({
  record,
  sections,
  completion,
  fullState,
  departments,
  programs,
}) => {
  return (
    <div className="details-view-grid mt-6">
      <DetailsCard
        icon={CheckCircle2}
        title="Progression globale"
        colorName="color-2"
        colSpan="full"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {completion.completedSections}/{completion.totalSections} sections complétées
            </span>
            <span className="text-sm text-muted-foreground">
              {completion.completedSectionsPercentage}%
            </span>
          </div>
          <div className="w-full h-3 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${completion.completedSectionsPercentage}%`,
                backgroundColor: completionBarColor(completion.completedSectionsPercentage),
              }}
            />
          </div>
        </div>
      </DetailsCard>

      <div className="section-card-full">
        <Collapsible
          title="Suivi inscription"
          subtitle={`Inscrit le ${formatDate(record.createdAt)} (${daysSince(record.createdAt)})`}
          defaultOpen
        >
          <div className="space-y-4">
            <div className="border-l-2 border-black pl-4 space-y-2">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                Inscription
              </h4>
              {fullState?.templates
                ?.slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((tpl) => {
                  const sectionData = sections[tpl.key] as any;
                  let pct = sectionData?.sectionCompletion?.completionPercentage ?? 0;
                  if (tpl.key === 'information_session' && sectionData?.hasParticipated === true) {
                    pct = 100;
                  }
                  const completionStatus = getCompletionStatus(pct);
                  const completionConfig = STEP_COMPLETION_CONFIG[completionStatus];
                  let stepDate: string | null | undefined;
                  if (tpl.key === 'information_session') {
                    stepDate = sectionData?.participatedAt;
                  } else if (tpl.key === 'contract') {
                    stepDate = sectionData?.signedDate ?? sectionData?.updatedAt;
                  } else {
                    stepDate = pct === 100 ? sectionData?.updatedAt : null;
                  }
                  return (
                    <div key={tpl.key} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        {TUTOR_REMOTE_STEP_LABELS[tpl.key] ?? (tpl as any).label ?? tpl.displayName}
                      </span>
                      <div className="flex items-center gap-3">
                        <LabelBadge
                          label={completionConfig.label}
                          icon={completionConfig.icon}
                          variant={completionConfig.badgeVariant}
                          color={completionConfig.badgeColor}
                          size="sm"
                        />
                        <span className="text-xs text-muted-foreground min-w-[70px] text-right">
                          {stepDate ? formatDate(stepDate) : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="border-l-2 border-orange-500 pl-4 space-y-2">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                Activation
              </h4>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Contrat signé</span>
                <span className="text-sm text-muted-foreground">
                  {(sections['contract'] as any)?.uploadDate
                    ? formatDate((sections['contract'] as any).uploadDate)
                    : 'En attente'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Première affectation</span>
                <span className="text-sm text-muted-foreground">Aucune affectation</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Première séance</span>
                <span className="text-sm text-muted-foreground">Aucune séance planifiée</span>
              </div>
            </div>
          </div>
        </Collapsible>
      </div>

      <DetailsCard icon={User} title="Informations" colorName="color-2" colSpan="full">
        <TutorInfoSectionContent sections={sections} departments={departments} />
      </DetailsCard>

      <DetailsCard icon={UserCheck} title="Réunion d'information" colorName="color-3" colSpan={1}>
        {(() => {
          const informationMeeting = sections['information_session'] as any;
          let pct = informationMeeting?.sectionCompletion?.completionPercentage ?? 0;
          if (informationMeeting?.hasParticipated === true) pct = 100;

          return (
            <>
              <div className="flex items-center justify-between mb-3">
                <CompletionBadge pct={pct} />
              </div>
              {informationMeeting ? (
                <div className="space-y-3">
                  <DetailField
                    label="Participation"
                    value={
                      <LabelBadge
                        label={informationMeeting.hasParticipated ? 'Participé' : 'Non participé'}
                        icon={informationMeeting.hasParticipated ? CheckCircle2 : XCircle}
                        variant="outlined"
                        color={
                          informationMeeting.hasParticipated
                            ? 'rgb(var(--color-success))'
                            : 'rgb(var(--color-warning))'
                        }
                        size="sm"
                      />
                    }
                  />
                  {informationMeeting.startDate && (
                    <DetailField
                      label="Planifiée le"
                      value={formatDate(informationMeeting.startDate)}
                    />
                  )}
                  {informationMeeting.hasParticipated && informationMeeting.markedBy && (
                    <DetailField
                      label="Marqué par"
                      value={
                        informationMeeting.markedBy?.userId === record.userId
                          ? "S'est marqué lui-même"
                          : `${informationMeeting.markedBy?.firstName || ''} ${informationMeeting.markedBy?.lastName || ''}`.trim() ||
                            '—'
                      }
                    />
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Aucune donnée de réunion d'information.
                </p>
              )}
            </>
          );
        })()}
      </DetailsCard>

      <DetailsCard icon={BookOpen} title="Accompagnement" colorName="color-1" colSpan="full">
        <TutorAccompagnementContent sections={sections} programs={programs} />
      </DetailsCard>

      <DetailsCard icon={FileCheck} title="Documents" colorName="color-2" colSpan={1}>
        {(() => {
          const myDocs = sections['my_documents'] as any;
          const pct = myDocs?.sectionCompletion?.completionPercentage ?? 0;
          const officialDocs = sections['official_documents'] as any;
          const criminalRecordDoc = officialDocs?.criminalRecord;
          const recordValidation = record.validation as any;
          const {
            status: criminalRecordStatus,
            validatedAt: criminalRecordValidatedAt,
            validatedBy: criminalRecordValidatedBy,
          } = resolveCriminalRecordDisplay({
            sectionCriminalRecord: criminalRecordDoc,
            recordValidation,
          });
          const certif = sections['certification'] as any;

          return (
            <>
              <div className="flex items-center justify-between mb-3">
                <CompletionBadge pct={pct} />
              </div>

              <div className="mb-4">
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                  Casier judiciaire
                </h4>
                {criminalRecordDoc ? (
                  <div className="space-y-3">
                    <DetailField
                      label="Casier soumis le"
                      value={formatDate(criminalRecordDoc.uploadDate)}
                    />
                    <DetailField
                      label="Numéro de casier"
                      value={criminalRecordDoc.recordNumber ?? criminalRecordDoc.number ?? '—'}
                    />
                    <DetailField
                      label="Date de délivrance"
                      value={formatDate(criminalRecordDoc.issueDate ?? criminalRecordDoc.issuedAt)}
                    />
                    <DetailField label="Délivré par" value={criminalRecordDoc.issuedBy ?? '—'} />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground min-w-[90px]">Statut</span>
                      {criminalRecordStatus === 'validated' && (
                        <LabelBadge label="Validé" icon={CheckCircle2} variant="active" size="sm" />
                      )}
                      {criminalRecordStatus === 'rejected' && (
                        <LabelBadge
                          label="Refusé"
                          icon={XCircle}
                          variant="outlined"
                          color="rgb(var(--color-error))"
                          size="sm"
                        />
                      )}
                      {criminalRecordStatus !== 'validated' &&
                        criminalRecordStatus !== 'rejected' && (
                          <LabelBadge
                            label="En attente"
                            icon={CheckCircle2}
                            variant="outlined"
                            color="rgb(var(--status-warning))"
                            size="sm"
                          />
                        )}
                    </div>
                    {criminalRecordStatus === 'validated' && criminalRecordValidatedAt && (
                      <>
                        <DetailField
                          label="Casier validé le"
                          value={formatDate(criminalRecordValidatedAt)}
                        />
                        <DetailField label="Validé par" value={criminalRecordValidatedBy ?? '—'} />
                      </>
                    )}
                    {criminalRecordStatus === 'rejected' && (
                      <>
                        <DetailField
                          label="Casier refusé le"
                          value={formatDate(criminalRecordDoc.validationDate)}
                        />
                        {criminalRecordDoc.rejectionReason && (
                          <DetailField
                            label="Motif refus"
                            value={criminalRecordDoc.rejectionReason}
                          />
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun casier soumis.</p>
                )}
              </div>

              <div className="pt-4 border-t border-border">
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                  Certification
                </h4>
                {certif && (certif.attestationNumber || certif.obtentionDate) ? (
                  <div className="space-y-3">
                    <DetailField label="Soumis le" value={formatDate(certif.uploadDate)} />
                    <DetailField label="Numéro" value={certif.attestationNumber} />
                    <DetailField label="Obtenu le" value={formatDate(certif.obtentionDate)} />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucune donnée</p>
                )}
              </div>
            </>
          );
        })()}
      </DetailsCard>

      <DetailsCard icon={FileText} title="Contrat" colorName="color-1" colSpan={1}>
        {(() => {
          const d = sections['contract'] as any;
          const pct = d?.sectionCompletion?.completionPercentage ?? 0;
          return (
            <>
              <div className="flex items-center justify-between mb-3">
                <CompletionBadge pct={pct} />
              </div>
              {d ? (
                <DetailField label="Signé le" value={formatDate(d.uploadDate) || '—'} />
              ) : (
                <p className="text-sm text-muted-foreground">Aucune donnée</p>
              )}
            </>
          );
        })()}
      </DetailsCard>
    </div>
  );
};
